import { type ButtonHTMLAttributes, forwardRef, type ReactNode, useMemo } from 'react'
import { cn } from '@util'
import { isDefined, isNotDefined } from '@terros/common'
import { Button as MButton, type ButtonProps as MButtonProps, createPolymorphicComponent, Tooltip } from '@mantine/core'
import { type MaterialSymbolType, Size, SizePrimitives, useTranslation } from '@front/model'
import { MaterialSymbolIcon, type MaterialSymbolIconProps } from '../terros/icon/MaterialSymbolIcon'
import { isIconButton } from './util'
import { type ButtonSize, type ButtonType, type ButtonVariant } from './types'
import classes from './button.module.css'

export type TButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof MButtonProps> &
  MButtonProps & {
    icon?: MaterialSymbolType
    iconProps?: Omit<MaterialSymbolIconProps, 'icon'>
    variant?: ButtonVariant
    size?: ButtonSize
    buttonType?: ButtonType
    iconPosition?: 'left' | 'right'
    rounded?: boolean
    tooltip?: string
    noTranslation?: boolean
  }

type SizeSpec = {
  fontSize?: string
  iconSize: number
  px?: string | number
  py?: string | number
}

const SIZE_SPEC: Record<ButtonSize, SizeSpec> = {
  xs: { iconSize: SizePrimitives['5'], px: Size.space['xl'], py: Size.space['xl'] },
  sm: { fontSize: 'text-md', iconSize: SizePrimitives['5'], px: Size.space['2xl'], py: Size.space['xl'] },
  md: { fontSize: 'text-md', iconSize: SizePrimitives['6'], px: Size.space['3xl'], py: Size.space['2xl'] },
  small: { fontSize: 'text-md', iconSize: SizePrimitives['5'], px: Size.space['2xl'], py: Size.space['xl'] },
  medium: { fontSize: 'text-md', iconSize: SizePrimitives['6'], px: Size.space['3xl'], py: Size.space['2xl'] },
}

const VARIANT_SPEC: Record<ButtonVariant, Pick<MButtonProps, 'variant' | 'color'>> = {
  default: { variant: 'filled', color: 'brand' },
  primary: { variant: 'filled', color: 'brand' },
  secondary: { variant: 'outline', color: 'brand' },
  gray: { variant: 'light', color: 'neutral' },
  accent: { variant: 'filled', color: 'yellow' },
  destructive: { variant: 'light', color: 'rose' },
  ghost: { variant: 'subtle', color: 'brand' },
  text: { variant: 'transparent', color: 'brand' },
}

const _TButton = forwardRef<HTMLButtonElement, TButtonProps>(
  ({ tooltip, noTranslation = false, children, ...props }, ref) => {
    const { t } = useTranslation()

    const translatedChildren = useMemo(() => {
      if (noTranslation || !children) return children
      if (typeof children === 'string') return t(children)
      if (Array.isArray(children)) return children.map((child) => (typeof child === 'string' ? t(child) : child))
      return children
    }, [children, noTranslation, t])

    const translatedTooltip = useMemo(() => {
      if (noTranslation || !tooltip) return tooltip
      return t(tooltip)
    }, [tooltip, noTranslation, t])

    if (translatedTooltip) {
      return (
        <Tooltip label={translatedTooltip}>
          <ButtonDisplay aria-label={translatedTooltip} {...props} ref={ref}>
            {translatedChildren}
          </ButtonDisplay>
        </Tooltip>
      )
    }

    return (
      <ButtonDisplay {...props} ref={ref}>
        {translatedChildren}
      </ButtonDisplay>
    )
  }
)

_TButton.displayName = 'TButton'

export const TButton = createPolymorphicComponent<'button', TButtonProps>(_TButton)

const ButtonDisplay = forwardRef<HTMLButtonElement, Omit<TButtonProps, 'tooltip' | 'noTranslation'>>(
  (
    {
      variant = 'primary',
      size: sizeProp = 'small',
      buttonType = 'default',
      rounded = false,
      children,
      icon,
      iconProps,
      iconPosition = 'left',
      leftSection,
      rightSection,
      className,
      justify = 'center',
      'aria-label': ariaLabel,
      ...props
    },
    ref
  ) => {
    const size = getSize(sizeProp)
    const spec = SIZE_SPEC[size]

    const isIconMode = useMemo(() => {
      if (isIconButton(buttonType)) return true
      if (isDefined(icon) && isNotDefined(children)) return true
      return false
    }, [buttonType, icon, children])
    const isText = variant === 'text'

    const iconEl: ReactNode = useMemo(() => {
      if (!icon) return null
      return <MaterialSymbolIcon icon={icon} size={spec.iconSize} {...iconProps} />
    }, [icon, spec.iconSize, iconProps])

    let left: ReactNode = leftSection
    let right: ReactNode = rightSection
    if (iconEl && !isIconMode) {
      if (iconPosition === 'left') left = leftSection ?? iconEl
      else right = rightSection ?? iconEl
    }

    const borderRadius = rounded || buttonType === 'icon-full' ? '9999px' : undefined

    const hasNoPadding = isIconMode || isText
    const isCentredByMantineHeight = typeof children === 'string'
    const px = hasNoPadding ? 0 : spec.px
    const py = hasNoPadding || isCentredByMantineHeight ? 0 : spec.py
    const calculatedProps = useMemo(() => {
      const variantSpec = VARIANT_SPEC[variant]
      return {
        ...variantSpec,
        justify: isIconMode ? 'center' : justify,
      }
    }, [variant, isIconMode, justify])

    const buttonChildren = useMemo(() => {
      if (isIconMode) return iconEl
      if (typeof children === 'string')
        return <span className={cn(spec.fontSize, 'truncate leading-normal')}>{children}</span>
      return children
    }, [isIconMode, iconEl, children, spec.fontSize])

    const height = useMemo(() => {
      if (isText) return 'fit-content'
      if (isIconMode) return undefined
      if (typeof children === 'string') return undefined
      return 'fit-content'
    }, [isText, isIconMode, children])

    const fallbackIconLabel = isIconMode && isDefined(icon) ? toIconLabel(icon) : undefined

    return (
      <MButton
        ref={ref}
        aria-label={ariaLabel ?? fallbackIconLabel}
        {...calculatedProps}
        leftSection={isIconMode ? undefined : left}
        rightSection={isIconMode ? undefined : right}
        className={cn(classes.button, className, 'capitalize')}
        data-icon-only={isIconMode || undefined}
        styles={{
          root: {
            paddingLeft: px,
            paddingRight: px,
            paddingTop: py,
            paddingBottom: py,
            aspectRatio: isIconMode ? 1 : undefined,
            borderRadius,
            height,
          },
          label: { fontSize: undefined, fontWeight: 500 },
        }}
        size={size}
        {...props}
      >
        {buttonChildren}
      </MButton>
    )
  }
)

ButtonDisplay.displayName = 'TButtonDisplay'

const getSize = (size: ButtonSize): ButtonSize => {
  if (size === 'small') return 'sm'
  if (size === 'medium') return 'md'
  return size
}

const toIconLabel = (icon: MaterialSymbolType): string => icon.replaceAll('_', ' ')
